import { FC, useState, useEffect, useMemo } from 'react';

import DataStatCard from '@components/modules/Admin/Dashboard/DataStatCard';
import { renderToString } from 'react-dom/server';
import { listCompanyUsers } from '@sdk/managers/UsersManager';
import { listPollsByOwner } from '@sdk/managers/PollsManager';
import { CompanyRole, Poll as APIPoll, PollStatus } from '@sdk/API';
import { useAuth } from '@hooks/Auth';
import { objectToQueryString } from '@utils/index';
import { endOfMonth, startOfMonth } from 'date-fns';
import { getCustomConfiguration } from '@sdk/managers/PollsManager';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import toast from 'react-hot-toast';
import { listPledgedUsersByPoll } from '@sdk/managers/PledgesManager';
import { listProxyVotesByPoll } from '@sdk/managers/VotesManager';
import { fetchPollDetails } from '@utils/Poll';
import ButtonMighty from '@components/elements/ButtonMighty';
import {
  Company,
  UserInfo,
  RepresentativesUsage,
  RecordPage,
  ProxiesUsage,
  PollInfo,
  PollTable,
} from '@components/modules/Report';
import { jsPDF } from 'jspdf';
import { off } from 'process';
import { getSubscribedPackage } from '@sdk/managers/PackageManager';
import { listStripeChargesByPoll } from '@sdk/managers/PaymentManager';
import moment from 'moment-timezone';
import flatten from 'lodash.flatten';

function getMoment(date = new Date(), tz = 'Asia/Singapore') {
  return moment(date).tz(tz);
}

const POLL_TYPE_FIELDS = [
  'id',
  'title',
  'status',
  'owner',
  'startTime',
  'endTime',
  'private',
  'hasPledge',
  'image',
  'winOption',
  'voteResults',
] as const;

const POLL_FIELDS = [
  ...POLL_TYPE_FIELDS.filter(
    (field) => !['image', 'voteResults', 'winOption'].includes(field)
  ),
  {
    winOption: ['id'],
  },
  { image: ['key', 'bucket', 'region'] },
  { voteResults: [{ items: ['count'] }] },
];

interface DashboardPoll
  extends Pick<Required<APIPoll>, typeof POLL_TYPE_FIELDS[number]> {
  startDate: Date;
  endDate: Date;
}

const AdminDashboardPage: FC = () => {
  const { loading, company, companyAdmin, dbUser } = useAuth();

  const [polls, setPolls] = useState<DashboardPoll[] | undefined>(undefined);
  var curentPage = 1;
  const [proxyVotes, setProxyVotes] = useState<any>();
  const [proxyPledges, setProxyPledges] = useState<any>();

  useEffect(() => {
    if (!dbUser) {
      return;
    }

    const init = async () => {
      try {
        if (loading) return;
        if (!companyAdmin || !company) {
          throw Error('Company not found.');
        }

        const pollsRes = (await listPollsByOwner(
          [
            {
              items: POLL_FIELDS,
            },
          ],
          companyAdmin.owner,
          {
            companyID: { eq: company.companyID },
          }
        )) as GraphQLResult<{ result: { items: any[] } }>;
        setPolls(
          pollsRes.data?.result?.items.map((poll) => {
            return {
              ...poll,
              startDate: new Date(poll.startTime),
              endDate: new Date(poll.endTime),
            };
          }) || []
        );
      } catch (err: any) {
        console.log('Failed init', err);
        toast.error(err?.message || err?.errors?.[0]?.message);
      }
    };

    init();
  }, [company, companyAdmin, dbUser, loading]);

  useEffect(() => {
    const init = async () => {
      if (!polls) return;

      let votes: any[] = [];
      let pledges: any[] = [];
      await Promise.all(
        polls.map(async (poll) => {
          const getVotes = async () => {
            const res = (await listProxyVotesByPoll(poll.id)) as GraphQLResult<{
              result: { items: any[] };
            }>;
            if (res.data && (res.data.result.items.length || 0) > 0) {
              votes = [...votes, ...res.data.result.items];
            }
          };
          const getPledges = async () => {
            const res = (await listPledgedUsersByPoll(poll.id, [
              'proxyUserID',
            ])) as GraphQLResult<{
              result: { items: any[] };
            }>;
            if (res.data && (res.data.result.items.length || 0) > 0) {
              pledges = [
                ...pledges,
                ...res.data.result.items.filter(
                  (pledge) => pledge.proxyUserID !== undefined
                ),
              ];
            }
          };
          const actions = [getVotes()];
          if (poll.hasPledge) actions.push(getPledges());
          await Promise.all(actions);
        })
      );
      setProxyPledges(pledges);
      setProxyVotes(votes);
    };
    init();
  }, [polls]);

  const monthProxyVotes = useMemo(() => {
    return (
      proxyVotes?.filter((vote) => {
        const createdAt = new Date(vote.createdAt);
        if (createdAt < startOfMonth(new Date())) return false;
        if (createdAt > endOfMonth(new Date())) return false;
        return true;
      }) || []
    );
  }, [proxyVotes]);

  const monthProxyPledges = useMemo(() => {
    return (
      proxyPledges?.filter((vote) => {
        const createdAt = new Date(vote.createdAt);
        if (createdAt < startOfMonth(new Date())) return false;
        if (createdAt > endOfMonth(new Date())) return false;
        return true;
      }) || []
    );
  }, [proxyPledges]);

  // fetch all polls
  const monthPolls = polls?.filter((poll) => {
    if (
      poll.startDate <= endOfMonth(new Date()) &&
      poll.endDate >= startOfMonth(new Date())
    ) {
      return true;
    }
    return false;
  });

  const InformationReport = (
    pdfContainer: HTMLDivElement,
    pollsData,
    subscriptionData
  ) => {
    const pdfElement = document.createElement('div');
    pdfElement.innerHTML = renderToString(
      <Company
        page={curentPage}
        data={{ pollsData, subscriptionData, company }}
        user={dbUser}
      />
    ).trim();
    pdfContainer.appendChild(pdfElement);
    curentPage++;
  };

  const UserInfoReport = (
    pdfContainer: HTMLDivElement,
    usersArray,
    proxiesArray,
    representativesArray,
    lengData
  ) => {
    let users = [...usersArray];
    let proxies = [...proxiesArray];
    let representatives = [...representativesArray];
    const pdfElement = document.createElement('div');
    let userInfoViews = [] as any;
    if (proxies.length + representatives.length + users.length <= 12) {
      if (representatives.length > 0)
        userInfoViews.push({ name: 'Representatives', array: representatives });
      if (proxies.length > 0)
        userInfoViews.push({ name: 'Proxy', array: proxies });
      if (users.length > 0) userInfoViews.push({ name: 'Users', array: users });
      users = [];
      proxies = [];
      representatives = [];
    } else {
      if (representatives.length >= 12) {
        userInfoViews = [
          {
            name: 'Representatives',
            array: [...representatives.filter((item, i) => i <= 11)],
          },
        ];
        representatives.splice(0, 12);
      } else {
        userInfoViews = [
          {
            name: 'Representatives',
            array: [...representatives],
          },
        ];
        representatives.splice(0, representatives.length);
        console.log(representatives);
        if (proxies.length >= 12 - userInfoViews[0].array.length) {
          userInfoViews = [
            ...userInfoViews,
            {
              name: 'Proxy',
              array: [
                ...proxies.filter(
                  (item, i) => i <= 12 - userInfoViews[0].array.length - 1
                ),
              ],
            },
          ];
          proxies.splice(0, 12 - userInfoViews[0].array.length);
        } else {
          userInfoViews = [
            ...userInfoViews,
            {
              name: 'Proxy',
              array: [...proxies],
            },
          ];
          proxies.splice(0, proxies.length);
          if (
            users.length >=
            12 - userInfoViews[0].array.length - userInfoViews[1].array.length
          ) {
            userInfoViews = [
              ...userInfoViews,
              {
                name: 'Users',
                array: [
                  ...users.filter(
                    (item, i) =>
                      i <=
                      12 -
                        userInfoViews[0].array.length -
                        userInfoViews[1].array.length -
                        1
                  ),
                ],
              },
            ];
            users.splice(
              0,
              12 - userInfoViews[0].array.length - userInfoViews[1].array.length
            );
          } else
            userInfoViews = [
              ...userInfoViews,
              {
                name: 'Users',
                array: [...users],
              },
            ];
          users.splice(0, users.length);
        }
      }
    }
    pdfElement.innerHTML = renderToString(
      <UserInfo
        user={dbUser}
        userInfoViews={userInfoViews}
        page={curentPage}
        lengData={lengData}
      />
    ).trim();
    pdfContainer.appendChild(pdfElement);
    curentPage++;
    let recordPages = Math.ceil(
      (representatives.length + proxies.length + users.length) / 20
    );
    for (let i = 0; i < recordPages; i++) {
      let records = [] as any;
      if (proxies.length + representatives.length + users.length <= 20) {
        if (representatives.length > 0)
          records.push({ name: 'Representatives', array: representatives });
        if (proxies.length > 0) records.push({ name: 'Proxy', array: proxies });
        if (users.length > 0) records.push({ name: 'Users', array: users });
      } else {
        if (representatives.length >= 20) {
          records = [
            {
              name: 'Representatives',
              array: [...representatives.filter((item, i) => i <= 19)],
            },
          ];
          representatives.splice(0, 20);
        } else {
          records = [
            {
              name: 'Representatives',
              array: [...representatives],
            },
          ];
          representatives.splice(0, representatives.length);
          if (proxies.length >= 20 - records[0].array.length) {
            records = [
              ...records,
              {
                name: 'Proxy',
                array: [
                  ...proxies.filter(
                    (item, i) => i <= 20 - records[0].array.length - 1
                  ),
                ],
              },
            ];
            proxies.splice(0, 20 - records[0].array.length);
          } else {
            records = [
              ...records,
              {
                name: 'Proxy',
                array: [...proxies],
              },
            ];
            proxies.splice(0, proxies.length);
            if (
              users.length >=
              20 - records[0].array.length - records[1].array.length
            ) {
              records = [
                ...records,
                {
                  name: 'Users',
                  array: [
                    ...users.filter(
                      (item, i) =>
                        i <=
                        20 -
                          records[0].array.length -
                          records[1].array.length -
                          1
                    ),
                  ],
                },
              ];
              users.splice(
                0,
                20 - records[0].array.length - records[1].array.length
              );
            } else {
              records = [
                ...records,
                {
                  name: 'Users',
                  array: [...users],
                },
              ];
              users.splice(0, users.length);
            }
          }
        }
      }

      let recordElement = document.createElement('div');
      recordElement.innerHTML = renderToString(
        <RecordPage user={dbUser} array={records} page={curentPage} />
      ).trim();

      pdfContainer.appendChild(recordElement);
      curentPage++;
    }
  };

  const UserInfoNextRecord = (
    pdfContainer: HTMLDivElement,
    users,
    proxies,
    representatives
  ) => {};

  const RepresentativesUsageReport = (
    pdfContainer: HTMLDivElement,
    representativesUsageArray,
    repLength
  ) => {
    let data = [...representativesUsageArray];
    let representativesUsageRecord;
    const pdfElement = document.createElement('div');
    if (data.length >= 16) {
      representativesUsageRecord = [...data.filter((item, i) => i < 16)];
      data.splice(0, 16);
    } else {
      representativesUsageRecord = [...data];
      data.splice(0, data.length);
    }

    pdfElement.innerHTML = renderToString(
      <RepresentativesUsage
        user={dbUser}
        array={representativesUsageRecord}
        page={curentPage}
        repLength={repLength}
      />
    ).trim();
    pdfContainer.appendChild(pdfElement);
    curentPage++;
    let recordPages = Math.ceil(data.length / 20);
    for (let i = 0; i < recordPages; i++) {
      let records = [] as any;
      let recordElement = document.createElement('div');
      if (data.length >= 20) {
        records = [...data.filter((item, i) => i <= 19)];
        data.splice(0, 20);
      } else records = [...data];

      recordElement.innerHTML = renderToString(
        <RecordPage
          user={dbUser}
          page={curentPage}
          array={[{ name: 'Representatives', array: records }]}
        />
      ).trim();
      pdfContainer.appendChild(recordElement);
      curentPage++;
    }
  };

  const ProxyUsageReport = (
    pdfContainer: HTMLDivElement,
    proxyData,
    proLength
  ) => {
    let data = [...proxyData];
    let recordInpage;
    const pdfElement = document.createElement('div');
    if (data.length >= 16) {
      recordInpage = [...data.filter((item, i) => i < 16)];
      data.splice(0, 16);
    } else {
      recordInpage = [...data];
      data.splice(0, data.length);
    }
    pdfElement.innerHTML = renderToString(
      <ProxiesUsage
        user={dbUser}
        array={recordInpage}
        page={curentPage}
        proLength={proLength}
      />
    ).trim();
    pdfContainer.appendChild(pdfElement);
    curentPage++;
    let recordPages = Math.ceil(data.length / 20);
    for (let i = 0; i < recordPages; i++) {
      let records = [] as any;
      let recordElement = document.createElement('div');
      if (data.length >= 20) {
        records = [...data.filter((item, i) => i <= 19)];
        data.splice(0, 20);
      } else records = [...data];

      recordElement.innerHTML = renderToString(
        <RecordPage
          user={dbUser}
          array={[{ name: 'Proxies', array: records }]}
          page={curentPage}
        />
      ).trim();
      pdfContainer.appendChild(recordElement);
      curentPage++;
    }
  };

  const PollInfoReport = (pdfContainer: HTMLDivElement, pollsData) => {
    const pdfElement = document.createElement('div');
    let data = [...(polls || [])];
    let recordInpage;
    if (data.length >= 5) {
      recordInpage = [...data.filter((item, i) => i < 5)];
      data.splice(0, 5);
    } else {
      recordInpage = [...data];
      data.splice(0, data.length);
    }
    pdfElement.innerHTML = renderToString(
      <PollInfo
        user={dbUser}
        data={recordInpage}
        pollsData={pollsData}
        page={curentPage}
      />
    ).trim();
    pdfContainer.appendChild(pdfElement);
    curentPage++;
    let recordPages = Math.ceil(data.length / 8);
    for (let i = 0; i < recordPages; i++) {
      let records = [] as any;
      let recordElement = document.createElement('div');
      if (data.length >= 8) {
        records = [...data.filter((item, i) => i < 8)];
        data.splice(0, 8);
      } else records = [...data];

      recordElement.innerHTML = renderToString(
        <PollTable user={dbUser} data={records} page={curentPage} />
      ).trim();
      pdfContainer.appendChild(recordElement);
      curentPage++;
    }
  };

  const exportPdf = async () => {
    const getSub = async (companyAdmin: any) => {
      try {
        const packages = await getSubscribedPackage(
          companyAdmin?.adminID || ''
        );
        const subscriptionData = {
          start: getMoment(
            new Date(packages.current_period_start * 1000)
          ).format('DD MMM YYYY'),
          end: getMoment(new Date(packages.current_period_end * 1000)).format(
            'DD MMM YYYY'
          ),
        };
        return subscriptionData;
      } catch (error) {
        return null;
      }
    };
    const companyUsers: any = await listCompanyUsers(
      company ? company.companyID : ''
    );
    // const packages = await getSubscribedPackage(companyAdmin?.adminID || '');
    // const subscriptionData = {
    //   start: getMoment(new Date(packages.current_period_start * 1000)).format(
    //     'DD MMM YYYY'
    //   ),
    //   end: getMoment(new Date(packages.current_period_end * 1000)).format(
    //     'DD MMM YYYY'
    //   ),
    // };
    const subscriptionData = await getSub(companyAdmin);
    const pollWithPledge = polls?.filter((x) => x.hasPledge);
    const pollWithoutPledge = polls?.filter((x) => !x.hasPledge);
    const closePolls = polls?.filter((x) => x.status === PollStatus.CLOSED);
    const deletedPolls = polls?.filter((x) => x.status === PollStatus.END);
    // @ts-ignore
    const openPolls = polls?.filter(
      (x: any) => ![PollStatus.CLOSED, PollStatus.END].includes(x.status)
    );
    console.log('pollWithPledge: ', pollWithPledge);
    const charges = await Promise.all(
      pollWithPledge?.map((p) => listStripeChargesByPoll(p.id)) || []
    );
    const allCharges = flatten(charges).filter(Boolean);
    console.log('charges: ', allCharges);
    const pollsData = {
      total: polls?.length,
      maximum: 1000,
      pollWithoutPledge: pollWithoutPledge?.length,
      pollWithPledge: pollWithPledge?.length,
      open: openPolls?.length,
      close: closePolls?.length,
      deleted: deletedPolls?.length,
      totalPledgeAmount: allCharges
        .map((x) => x.amount)
        .reduce((a, b) => a + +b, 0),
    };
    const companyUserData = companyUsers?.data?.result?.items || [];
    console.log(companyUserData);
    const repData = companyUserData
      .filter((x) => x.role === CompanyRole.REPRESENTATIVE)
      .map((x) => {
        return `${x.user.firstName || ''} ${x.user.lastName || ''} ${
          x.user.email
        }`;
      });
    const proData = companyUserData
      .filter((x) => x.role === CompanyRole.PROXY)
      .map((x) => {
        return `${x.user.firstName || ''} ${x.user.lastName || ''} ${
          x.user.email
        }`;
      });
    const uData = companyUserData
      .filter((x) => x.role === CompanyRole.EMPLOYEE)
      .map((x) => {
        return `${x.user.firstName || ''} ${x.user.lastName || ''} ${
          x.user.email
        }`;
      });

    let users = [...uData];
    let proxies = [...proData];
    let representatives = [...repData];
    let representativesUsageArray = [...repData];
    let proxyUsageData = [...proData];
    var pdfContainer = document.createElement('div');
    InformationReport(pdfContainer, pollsData, subscriptionData);
    UserInfoReport(pdfContainer, users, proxies, representatives, {
      repData,
      proData,
      uData,
    });
    UserInfoNextRecord(pdfContainer, users, proxies, representatives);
    RepresentativesUsageReport(
      pdfContainer,
      representativesUsageArray,
      repData.length
    );
    ProxyUsageReport(pdfContainer, proxyUsageData, proData.length);
    console.log(pollsData);
    PollInfoReport(pdfContainer, pollsData);
    pdfContainer.classList.add('report-pdf-container');
    let pdf = new jsPDF('p', 'pt', 'A4');
    pdf.html(pdfContainer, {
      fontFaces: [
        {
          family: 'Space Grotesk',
          src: [
            {
              format: 'truetype',
              url: '/space-grotesk.ttf',
            },
          ],
        },
        {
          family: 'Arial',
          src: [
            {
              format: 'truetype',
              url: '/arial.ttf',
            },
          ],
        },
      ],
      callback: () => {
        var pageCount = pdf.internal.pages.length - 1;
        pdf.deletePage(pageCount);
        pdf.save('PollReport_' + Date.now() + '.pdf');
        curentPage = 1;
      },
    });
  };

  return (
    <div className="h-screen pb-24 overflow-x-hidden overflow-y-scroll bg-fixed bg-purple bracket-pattern-purple lg:pb-0">
      <div className="mx-4 mt-16">
        <h1 className="mb-4 text-5xl whitespace-pre-line text-dashboard-title">
          {`Dashboard`}
        </h1>
      </div>
      <div className="flex justify-end px-4 lg:px-0">
        <div style={{ width: '200px' }}>
          <ButtonMighty
            color="#f97075"
            text={`Export`}
            borderColor="#221f20"
            margin="0"
            textColor="#221f20"
            width="fit-content"
            onClick={exportPdf}
          />
        </div>
      </div>
      <div className="mx-4 space-y-2" id="pdf-content">
        <h2 className="pt-4 text-4xl uppercase text-dashboard-title-polls">
          Polls
        </h2>
        <div className="flex pb-2 space-x-4">
          <DataStatCard
            className="w-1/4"
            iconCn="group-hover:text-cyan"
            href={`/admin/polls`}
            label="Poll Usage"
            dataKey={company ? ['/stats/poll/usage', company.companyID] : null}
            fetch={async () => {
              if (!polls || !company) return '...';
              const customConfig: any = await getCustomConfiguration(
                company?.companyID
              );
              console.log('custom config', customConfig);
              return polls.length.toString();
            }}
          />
          <DataStatCard
            className="w-1/4"
            iconCn="group-hover:text-cyan"
            href={`/admin/polls${objectToQueryString({
              status: 'ONGOING',
              private: 0,
            })}`}
            label="Ongoing Public Polls"
            dataKey={
              company ? ['/stats/poll/public/ongoing', company.companyID] : null
            }
            fetch={async () => {
              if (!polls) return '...';
              const res = polls.filter((poll) => {
                if (poll.private !== 0) return false;
                if (
                  poll.status &&
                  [PollStatus.CLOSED, PollStatus.END].includes(poll.status)
                )
                  return false;
                return true;
              });
              return res.length.toString();
            }}
          />
          <DataStatCard
            className="w-1/4"
            iconCn="group-hover:text-cyan"
            href={`/admin/polls${objectToQueryString({
              status: 'ONGOING',
              private: 1,
            })}`}
            label="Ongoing Private Polls"
            dataKey={
              company
                ? ['/stats/poll/private/ongoing', company.companyID]
                : null
            }
            fetch={async () => {
              if (!polls) return '...';
              const res = polls.filter((poll) => {
                if (poll.private !== 1) return false;
                if (
                  poll.status &&
                  [PollStatus.CLOSED, PollStatus.END].includes(poll.status)
                )
                  return false;
                return true;
              });

              return res.length.toString();
            }}
          />
          <DataStatCard
            className="w-1/4"
            iconCn="group-hover:text-cyan"
            href={`/admin/polls${objectToQueryString({
              status: 'ONGOING',
              private: 2,
            })}`}
            label="Ongoing Enterprise Polls"
            dataKey={
              company ? ['/stats/poll/public/ongoing', company.companyID] : null
            }
            fetch={async () => {
              if (!polls) return '...';
              const res = polls.filter((poll) => {
                if (poll.private !== 2) return false;
                if (
                  poll.status &&
                  [PollStatus.CLOSED, PollStatus.END].includes(poll.status)
                )
                  return false;
                return true;
              });
              return res.length.toString();
            }}
          />
          <DataStatCard
            className="w-1/4"
            href={`/admin/polls${objectToQueryString({
              startDate: Math.floor(startOfMonth(new Date()).getTime() / 1000),
              endDate: Math.floor(endOfMonth(new Date()).getTime() / 1000),
            })}`}
            iconCn="group-hover:text-cyan"
            label="Polls - Month"
            fetch={async () => {
              if (!dbUser) return '...';
              return monthPolls?.length;
            }}
          />
        </div>
        <h2 className="pt-4 text-4xl uppercase text-dashboard-title-pledging">
          PLEDGING
        </h2>
        <div className="flex pb-2 space-x-4">
          <DataStatCard
            className="w-1/2"
            iconCn="group-hover:text-yellow"
            href={`/admin/polls${objectToQueryString({
              pledging: true,
              status: PollStatus.PLEDGING1,
            })}`}
            label="Pledge Amount - Ongoing"
            // fetch={async () => {
            //   if (!polls) return '...';

            //   const _pledgingPolls = polls.filter(
            //     (poll) => poll.status === PollStatus.PLEDGING1
            //   );

            //   const total = 0;
            //   for (const poll of _pledgingPolls) {
            //     const pledges = await listPledgedUsersByPoll(poll.id, ['id']);
            //     console.log(`Pledges for ${poll.title}`, pledges);
            //   }
            //   return total;
            // }}
            fetch={async () => {
              if (!polls) return '...';

              const pledgingPolls = monthPolls?.filter(
                (poll) => poll.hasPledge && poll.status === PollStatus.PLEDGING1
              );

              console.log('Pldege polls', pledgingPolls);

              let amount = 0;
              if (pledgingPolls) {
                let res: number[] = await Promise.all(
                  pledgingPolls.map(async (poll) => {
                    const [pollDetails, pollPledgesRes] = await Promise.all([
                      fetchPollDetails('', poll.id),
                      listPledgedUsersByPoll(poll.id, ['id']) as Promise<
                        GraphQLResult<{ result: { items: any[] } }>
                      >,
                    ]);
                    if (!pollDetails?.poll) return 0;

                    let pledgeAmount = parseFloat(
                      pollDetails?.poll?.winOption?.detail?.price || '0.00'
                    );
                    return (
                      (pollPledgesRes.data?.result.items.length || 0) *
                      pledgeAmount
                    );
                  })
                );
                amount = res.reduce((sum: number, n: number) => sum + n, 0);
              }
              return `$${amount}`;
            }}
          />
          <DataStatCard
            className="w-1/2"
            iconCn="group-hover:text-yellow"
            href={`/admin/polls${objectToQueryString({
              startDate: Math.floor(startOfMonth(new Date()).getTime() / 1000),
              endDate: Math.floor(endOfMonth(new Date()).getTime() / 1000),
              pledging: true,
            })}`}
            label="Pledges - Month"
            fetch={async () => {
              if (!polls) return '...';

              const pledgingPolls = monthPolls?.filter(
                (poll) => poll.hasPledge && poll.status === PollStatus.PLEDGING1
              );

              if (pledgingPolls) {
                let res: number[] = await Promise.all(
                  pledgingPolls.map(async (poll) => {
                    const pollPledgesRes = (await listPledgedUsersByPoll(
                      poll.id,
                      ['proxyUserID']
                    )) as GraphQLResult<{ result: { items: any[] } }>;
                    return pollPledgesRes.data?.result.items.length || 0;
                  })
                );

                return res.reduce((sum: number, n: number) => sum + n, 0);
              }
              return 0;
            }}
          />
        </div>

        <h2 className="pt-4 text-4xl uppercase text-dashboard-title-company">
          COMPANY
        </h2>
        <div className="flex pb-2 space-x-4">
          <DataStatCard
            className="w-1/4"
            iconCn="group-hover:text-orange"
            href={`/admin/representatives`}
            label="Representatives"
            fetch={async () => {
              if (!polls || !company) return '...';

              const reps = (await listCompanyUsers(
                company.companyID,
                CompanyRole.REPRESENTATIVE
              )) as GraphQLResult<{ result: { items: any[] } }>;

              return reps.data?.result.items.length || '0';
            }}
          />
          <DataStatCard
            className="w-1/4"
            iconCn="group-hover:text-orange"
            href={`/admin/proxy`}
            label="Proxies"
            fetch={async () => {
              if (!polls || !company) return '...';

              const proxies: any = (await listCompanyUsers(
                company.companyID,
                CompanyRole.PROXY
              )) as GraphQLResult<{ result: { items: any[] } }>;
              return proxies.data?.result.items.length || '0';
            }}
          />
          <DataStatCard
            className="w-1/4"
            iconCn="group-hover:text-orange"
            href={`/admin/polls`}
            label="Proxied Votes - Month"
            fetch={async () => {
              if (!polls) return '...';
              return monthProxyVotes?.length || 0;
            }}
          />
          <DataStatCard
            className="w-1/4"
            iconCn="group-hover:text-orange"
            href={`/admin/polls`}
            label="Proxied Pledges - Month"
            fetch={async () => {
              if (!polls) return '...';

              // const pledgingPolls = monthPolls?.filter(
              //   (poll) => poll.hasPledge
              // );

              // if (pledgingPolls) {
              //   let res: number[] = await Promise.all(
              //     pledgingPolls.map(async (poll) => {
              //       const pollPledgesRes = (await listPledgedUsersByPoll(
              //         poll.id,
              //         ['proxyUserID']
              //       )) as GraphQLResult<{ result: { items: any[] } }>;
              //       console.log('pledge users', { pollPledgesRes });
              //       return (
              //         pollPledgesRes.data?.result.items.filter(
              //           (pledge) => pledge.proxyUserID !== undefined
              //         ).length || 0
              //       );
              //     })
              //   );
              //   return res.reduce((sum: number, n: number) => sum + n, 0);
              // }
              return monthProxyPledges?.length || 0;
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
